import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Cookie, Settings, X, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CONSENT_KEY = "cookie_consent";
const CONSENT_VERSION = "1.0";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: true,
  });

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
    } else {
      try {
        const parsed = JSON.parse(consent);
        if (parsed.version !== CONSENT_VERSION) {
          setShowBanner(true);
        } else {
          setPreferences(parsed.preferences);
        }
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    const consent = {
      version: CONSENT_VERSION,
      preferences: prefs,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: true,
    };
    setPreferences(necessaryOnly);
    saveConsent(necessaryOnly);
  };

  const saveCustomPreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-3 md:p-4 bg-white border-t shadow-lg">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-3 md:gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-brand-100 rounded-lg shrink-0 hidden md:block">
                  <Cookie className="h-6 w-6 text-brand-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1 md:mb-2 text-sm md:text-base">Témoins de connexion (cookies)</h3>
                  <p className="text-xs md:text-sm text-slate-600">
                    Ce site web utilise des témoins de connexion (cookies). Avant leur utilisation, vous devez consentir expressément à leur installation en configurant vos préférences ci-dessous.
                  </p>
                  <p className="text-xs md:text-sm text-slate-600 mt-2 hidden md:block">
                    Les témoins de connexion, si vous y consentez, nous permettront de : (1) personnaliser le contenu et les annonces selon vos préférences, (2) activer des fonctionnalités relatives aux médias sociaux, et (3) analyser le trafic. Avec votre consentement explicite et distinct, nous pourrons partager certaines informations sur votre utilisation du site avec nos partenaires de médias sociaux, de publicité et d'analyse, dont la liste complète est disponible dans notre{' '}
                    <Link to="/politique-confidentialite" className="text-brand-600 hover:underline">
                      politique de confidentialité
                    </Link>.
                  </p>
                  <p className="text-xs md:text-sm text-slate-600 mt-2 hidden md:block">
                    Vous avez le droit de retirer votre consentement à tout moment. Pour plus d'informations sur vos droits et la protection de vos renseignements personnels, veuillez consulter notre{' '}
                    <Link to="/politique-confidentialite" className="text-brand-600 hover:underline">
                      politique de confidentialité
                    </Link>.
                  </p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-2 md:justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} className="gap-2 w-full md:w-auto text-xs md:text-sm border-gray-300 text-gray-700">
                  <Settings className="h-4 w-4" />
                  Configurer
                </Button>
                <Button variant="outline" size="sm" onClick={acceptNecessaryOnly} className="w-full md:w-auto text-xs md:text-sm border-gray-300 text-gray-700">
                  Refuser
                </Button>
                <Button size="sm" onClick={acceptAll} className="bg-brand-600 hover:bg-brand-700 text-white w-full md:w-auto text-xs md:text-sm">
                  Tout accepter
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-brand-600" />
              Paramètres des témoins (cookies)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <p className="text-sm text-slate-600">
              Conformément à la Loi 25 sur la protection des renseignements personnels, vous pouvez gérer vos préférences de témoins ci-dessous.
            </p>

            {/* Necessary Cookies */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="font-medium">Témoins essentiels</Label>
                <p className="text-sm text-slate-500 mt-1">
                  Nécessaires au fonctionnement du site. Ne peuvent pas être désactivés.
                </p>
              </div>
              <Switch checked={true} disabled />
            </div>

            {/* Functional Cookies */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="font-medium">Témoins fonctionnels</Label>
                <p className="text-sm text-slate-500 mt-1">
                  Permettent de mémoriser vos préférences (langue, région).
                </p>
              </div>
              <Switch
                checked={preferences.functional}
                onCheckedChange={(checked) => setPreferences({ ...preferences, functional: checked })}
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="font-medium">Témoins analytiques</Label>
                <p className="text-sm text-slate-500 mt-1">
                  Nous aident à comprendre comment vous utilisez le site.
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) => setPreferences({ ...preferences, analytics: checked })}
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="font-medium">Témoins publicitaires</Label>
                <p className="text-sm text-slate-500 mt-1">
                  Utilisés pour vous proposer des publicités pertinentes.
                </p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
              />
            </div>
          </div>

          <div className="flex justify-between gap-2 pt-4 border-t">
            <Button variant="outline" onClick={acceptNecessaryOnly}>
              Essentiels seulement
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={acceptAll}>
                Tout accepter
              </Button>
              <Button onClick={saveCustomPreferences} className="bg-brand-600 hover:bg-brand-700">
                Enregistrer mes choix
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
